import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hook";
import { fetchDistributorPurchaseOrders, createPurchaseOrder, getDistributorById } from "../../features/distributor/DistributorSlice";
import { ChevronDown, FileText, Hash, Calendar, AlertCircle, ArrowLeft, Trash, Loader2 } from "lucide-react";
import CustomModal from "../common/CustomModal";

interface Distributor {
  distributorId: number;
  name: string;
}

interface PurchaseOrdersPageProps {
  distributor: Distributor;
  onBack: () => void;
}

const PurchaseOrdersPage: React.FC<PurchaseOrdersPageProps> = ({ distributor, onBack }) => {
  const dispatch = useAppDispatch();
  const { loading, error, purchaseOrders } = useAppSelector((state) => state.distributor);
  const selectedDistributor = useAppSelector((state) => state.distributor.selectedDistributor);
  const availablePrograms = selectedDistributor?.wallet || [];

  // State for create PO form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [form, setForm] = useState<{
    clientPoNumber: string;
    clientOrderDate: string;
    receivedDate: string;
    remarks: string;
    lineItems: { programId: string; denominationAmount: string; quantity: string }[];
    invoice: File | null;
  }>({
    clientPoNumber: "",
    clientOrderDate: "",
    receivedDate: "",
    remarks: "",
    lineItems: [{ programId: "", denominationAmount: "", quantity: "" }],
    invoice: null,
  });
  const [formErrors, setFormErrors] = useState<any>({});
  const [submitting, setSubmitting] = useState(false);

  // State to manage expanded cards
  const [expandedCards, setExpandedCards] = useState<{ [poId: number]: boolean }>({});
  // Add state for invoice preview
  const [invoicePreview, setInvoicePreview] = useState<{ url: string, poKey: string } | null>(null);

  useEffect(() => {
    dispatch(getDistributorById(distributor.distributorId));

  }, []);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, idx?: number) => {
    const { name, value, files } = e.target as any;
    if (name === "invoice") {
      setForm((prev) => ({ ...prev, invoice: files[0] }));
    } else if (name.startsWith("lineItems") && typeof idx === "number") {
      const key = name.split(".")[1] as "programId" | "denominationAmount" | "quantity";
      setForm((prev) => {
        const newLineItems = [...prev.lineItems];
        newLineItems[idx][key] = value;
        return { ...prev, lineItems: newLineItems };
      });
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleAddLineItem = () => {
    setForm((prev) => ({ ...prev, lineItems: [...prev.lineItems, { programId: "", denominationAmount: "", quantity: "" }] }));
  };
  const handleRemoveLineItem = (idx: number) => {
    setForm((prev) => ({ ...prev, lineItems: prev.lineItems.filter((_, i) => i !== idx) }));
  };

  const validateForm = () => {
    const errors: any = {};
    if (!form.clientPoNumber.trim()) errors.clientPoNumber = "PO Number is required";
    if (!form.clientOrderDate) errors.clientOrderDate = "Client Order Date is required";
    if (!form.receivedDate) errors.receivedDate = "Fincentive Order Date is required";
    if (!form.remarks.trim()) errors.remarks = "Remarks are required.";
    if (!form.invoice) errors.invoice = "Invoice (PDF) is required.";
    form.lineItems.forEach((li, i) => {
      if (!li.programId) errors[`lineItems.${i}.programId`] = "Program is required.";
      if (!li.denominationAmount || isNaN(Number(li.denominationAmount))) errors[`lineItems.${i}.denominationAmount`] = "Required";
      if (!li.quantity || isNaN(Number(li.quantity))) errors[`lineItems.${i}.quantity`] = "Required";
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0 ? null : errors;
  };

  const handleCreatePO = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateForm();
    if (errors) {
      return;
    }
    setSubmitting(true);
    try {
      const lineItems = form.lineItems.map((li) => ({
        programId: Number(li.programId),
        denominationAmount: Number(li.denominationAmount),
        quantity: Number(li.quantity),
      }));
      await dispatch(createPurchaseOrder({
        distributorId: distributor.distributorId,
        lineItems,
        invoice: form.invoice,
        remarks: form.remarks,
        clientOrderDate: form.clientOrderDate,
        clientPoNumber: form.clientPoNumber,
        receivedDate: form.receivedDate,
      }) as any);
      setShowCreateForm(false);
      setForm({ clientPoNumber: "", clientOrderDate: "", receivedDate: "", remarks: "", lineItems: [{ programId: "", denominationAmount: "", quantity: "" }], invoice: null });
      dispatch(fetchDistributorPurchaseOrders({ distributorId: distributor.distributorId }));
    } catch (err: any) {
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (distributor?.distributorId) {
      dispatch(fetchDistributorPurchaseOrders({ distributorId: distributor.distributorId }));
    }
  }, [dispatch, distributor]);

  // Auto-select first program for new line item
  useEffect(() => {
    if (availablePrograms.length > 0) {
      setForm((prev) => ({
        ...prev,
        lineItems: prev.lineItems.map((li, idx) =>
          li.programId === "" ? { ...li, programId: String(availablePrograms[0].programId) } : li
        ),
      }));
    }
  }, [availablePrograms.length]);

  // Add a status color mapping helper
  const statusColorMap: Record<string, string> = {
    'pending': 'bg-blue-100 text-blue-700',
    'failed': 'bg-red-100 text-red-700',
    'completed': 'bg-brand/10 text-brand',
    'default': 'bg-gray-100 text-gray-600',
  };

  return (
    <div className="bg-white h-full">
      {/* Header */}
      <div className="flex border-b border-cardborder mb-8 flex-col md:flex-row md:items-center md:justify-between max-w-7xl mx-auto px-7 py-8">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Purchase Orders
            </h1>
            <div className="text-sm text-gray-500 mt-1">
              {distributor.name}
            </div>
          </div>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-md hover:bg-brand/80 focus:outline-none mt-4 md:mt-0"
        >
          Create Order
        </button>
      </div>

      {/* Create PO Form Modal */}
      {showCreateForm && (
        <CustomModal
          show={showCreateForm}
          onHide={() => setShowCreateForm(false)}
          title="Create Order"
          width="w-[600px]"
        >
          <form id="create-po-form" onSubmit={handleCreatePO} className="space-y-5 w-full">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">PO Number</label>
              <input
                name="clientPoNumber"
                value={form.clientPoNumber}
                onChange={handleFormChange}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none transition-colors ${formErrors.clientPoNumber ? 'border-red-300' : 'border-gray-300'}`}
                placeholder="Enter PO number"
              />
              {formErrors.clientPoNumber && <p className="mt-1 text-sm text-red-600">{formErrors.clientPoNumber}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Invoice (PDF)</label>
              <input
                name="invoice"
                type="file"
                accept="application/pdf"
                onChange={handleFormChange}
                className={`block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 mb-2 ${formErrors.invoice ? 'border-red-300' : ''}`}
              />
              {formErrors.invoice && <p className="mt-1 text-sm text-red-600">{formErrors.invoice}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client Order Date</label>
              <input
                name="clientOrderDate"
                type="date"
                value={form.clientOrderDate}
                onChange={handleFormChange}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none transition-colors ${formErrors.clientOrderDate ? 'border-red-300' : 'border-gray-300'}`}
                placeholder="Select order date"
              />
              {formErrors.clientOrderDate && <p className="mt-1 text-sm text-red-600">{formErrors.clientOrderDate}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fincentive Order Date</label>
              <input
                name="receivedDate"
                type="date"
                value={form.receivedDate}
                onChange={handleFormChange}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none transition-colors ${formErrors.receivedDate ? 'border-red-300' : 'border-gray-300'}`}
                placeholder="Select received date"
              />
              {formErrors.receivedDate && <p className="mt-1 text-sm text-red-600">{formErrors.receivedDate}</p>}
            </div>

            <div>
              <div className="flex gap-4 mb-1">
                <label className="w-1/2 block text-sm font-medium text-gray-700 mb-1">Line Items</label>
                <label className="w-1/4 ml-4 block text-sm font-medium text-gray-700 mb-1 text-center">Denomination</label>
                <label className="w-1/4 block text-sm font-medium text-gray-700 mb-1 text-center">Quantity</label>
                <div className="w-8" />
              </div>
              {form.lineItems.map((li, idx) => (
                <div key={idx} className="relative flex justify-between items-center gap-4 mb-2">
                  {availablePrograms.length > 0 && (
                    <div className="relative w-1/2">
                      <select
                        name="lineItems.programId"
                        value={li.programId}
                        onChange={e => handleFormChange(e, idx)}
                        className={`appearance-none pl-4 pr-10 py-2 text-sm border rounded-md focus:outline-none transition-colors border-gray-300 w-full ${formErrors[`lineItems.${idx}.programId`] ? 'border-red-300' : 'border-gray-300'}`}
                      >
                        <option value="">Select Program</option>
                        {availablePrograms.map((program) => (
                          <option key={program.programId} value={program.programId}>
                            {program.programName}
                          </option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-gray-500">
                        <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                          <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                        </svg>
                      </div>
                      {formErrors[`lineItems.${idx}.programId`] && <p className="mt-1 text-xs text-red-600">{formErrors[`lineItems.${idx}.programId`]}</p>}
                    </div>
                  )}
                  <input
                    name="lineItems.denominationAmount"
                    placeholder="Denomination"
                    value={li.denominationAmount}
                    onChange={e => handleFormChange(e, idx)}
                    className={`w-1/4 px-3 py-2 text-sm border rounded-md focus:outline-none transition-colors ${formErrors[`lineItems.${idx}.denominationAmount`] ? 'border-red-300' : 'border-gray-300'}`}
                  />
                  {formErrors[`lineItems.${idx}.denominationAmount`] && <p className="mt-1 text-xs text-red-600 absolute left-1/2 -bottom-5">{formErrors[`lineItems.${idx}.denominationAmount`]}</p>}
                  <input
                    name="lineItems.quantity"
                    placeholder="Quantity"
                    value={li.quantity}
                    onChange={e => handleFormChange(e, idx)}
                    className={`w-1/4 px-3 py-2 text-sm border rounded-md focus:outline-none transition-colors ${formErrors[`lineItems.${idx}.quantity`] ? 'border-red-300' : 'border-gray-300'}`}
                  />
                  {formErrors[`lineItems.${idx}.quantity`] && (
                    <p className="mt-1 text-xs text-red-600 absolute right-20 -bottom-5">{formErrors[`lineItems.${idx}.quantity`]}</p>
                  )}
                  {form.lineItems.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveLineItem(idx)}
                      className="text-red-500 text-sm px-2"
                    >
                      <Trash className="w-4 h-4" />
                    </button>
                  )}
                </div>

              ))}
              <button
                type="button"
                onClick={handleAddLineItem}
                className="inline-flex items-center gap-1 px-3 py-1.5 mt-1 text-sm font-medium text-brand border border-brand rounded-md hover:bg-brand hover:text-white focus:outline-none transition-colors"
              >
                Add Line Item
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
              <input
                name="remarks"
                value={form.remarks}
                onChange={handleFormChange}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none transition-colors ${formErrors.remarks ? 'border-red-300' : 'border-gray-300'}`}
                placeholder="Enter remarks (optional)"
              />
              {formErrors.remarks && <p className="mt-1 text-sm text-red-600">{formErrors.remarks}</p>}
            </div>
            <div className="flex justify-between pt-2">
              <button
                type="button"
                className="px-4 py-2 rounded-md text-sm font-medium text-white bg-brand hover:bg-brand/80 focus:outline-none focus:border-0 focus:ring-1 focus:ring-brand transition-colors"
                onClick={() => setShowCreateForm(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="create-po-form"
                disabled={submitting}
                className="px-4 py-2 rounded-md text-sm font-medium text-white bg-brand hover:bg-brand/80 focus:outline-none focus:border-0 focus:ring-1 focus:ring-brand transition-colors disabled:opacity-50"
              >
                {submitting ? "Creating..." : "Create PO"}
              </button>
            </div>
          </form>
        </CustomModal>
      )}

      {/* Content Section */}
      <div className="max-w-7xl mx-auto px-8 sm:px-6 lg:px-8">
        {/* Error State */}
        {error && (
          <div className="bg-white rounded-xl p-6 flex flex-col justify-center min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder mb-8">
            <div className="flex items-center gap-2 justify-center text-red-500">
              <AlertCircle className="w-5 h-5" />
              <span>{error ? error : "Something went wrong!"}</span>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-xl p-6 flex flex-col justify-center min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder mb-8">
            <div className="flex justify-center items-center gap-2">
              <Loader2 className="w-10 h-10 animate-spin text-brand/80" />
              <span className="text-sm text-gray-500">Loading...</span>
            </div>
          </div>
        )}

        {/* Purchase Orders List */}
        {!loading && !error && purchaseOrders && purchaseOrders.transactions.length > 0 && (
          <div className="space-y-6 mb-8">
            {purchaseOrders.transactions.map((po) => (
              <div
                key={po.purchaseOrderId}
                className="rounded-2xl border border-cardborder shadow-sm bg-white hover:shadow-md transition-shadow duration-200"
              >
                {/* Card Header */}
                <div
                  className="flex items-center justify-between px-8 py-6 rounded-t-2xl cursor-pointer select-none  border-b border-cardborder group"
                  onClick={() =>
                    setExpandedCards((prev) => ({
                      ...prev,
                      [po.purchaseOrderId]: !prev[po.purchaseOrderId],
                    }))
                  }
                >
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-4 mb-1">
                      <Hash className="w-5 h-5 text-gray-400" />
                      <span className="text-lg font-bold text-gray-900 tracking-tight">{po.clientPoNumber}</span>
                      <span
                        className={`ml-2 px-2 py-0.5 rounded-full text-xs font-semibold capitalize tracking-wide ${statusColorMap[po.status?.toLowerCase?.()] || statusColorMap['default']}`}
                      >
                        {po.status}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm text-gray-700 font-medium">
                      <span>Client Order: <span className="font-semibold text-brand">{new Date(po.clientOrderDate).toLocaleDateString()}</span></span>
                      <span>Fincentive Order: <span className="font-semibold text-brand">{new Date(po.receivedDate).toLocaleDateString()}</span></span>
                      <span>Line Items: <span className="font-semibold text-brand">{po.lineItems.length}</span></span>
                      <span>
                        Invoice: {po.invoice && (
                          <button
                            type="button"
                            className="px-3 py-1 ml-1 rounded-full bg-brand text-white text-xs font-semibold hover:bg-brand/80 transition-colors"
                            onClick={e => {
                              e.stopPropagation();
                              setInvoicePreview({ url: po.invoice || '', poKey: po.clientPoNumber });
                            }}
                          >
                            Preview
                          </button>
                        )}
                      </span>
                    </div>
                    <div className="text-sm gap-x-8 gap-y-2 text-gray-500 mt-1 flex items-center gap-2">
                      <span>Remarks: <span className="font-medium text-gray-700">{po.remarks}</span></span>
                    </div>
                  </div>
                  <ChevronDown className={`w-7 h-7 text-gray-400 ml-6 transition-transform duration-200 group-hover:text-brand ${expandedCards[po.purchaseOrderId] ? 'rotate-180 text-brand' : ''}`} />
                </div>
                {/* Expanded Section */}
                {expandedCards[po.purchaseOrderId] && (
                  <div className="px-8 pb-8 pt-4">
                    <div className="mb-4 mt-2 text-base font-bold text-gray-900 tracking-wide">Line Items</div>
                    <div className="flex flex-col gap-4">
                      {po.lineItems.map((li, idx) => {
                        const program = availablePrograms.find(p => String(p.programId) === String(li.programId));
                        return (
                          <div
                            key={li.purchaseOrderLineItemId}
                            className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-5 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-150"
                          >
                            {/* Left: Program Name */}
                            <div className="flex-1 min-w-[120px]">
                              <div className="text-xs text-gray-500 font-medium">Program</div>
                              <div className="text-base font-bold text-gray-900">{program ? program.programName : li.programId}</div>
                            </div>
                            {/* Right: Stats */}
                            <div className="flex flex-row flex-wrap md:flex-nowrap gap-6 items-center justify-end w-full md:w-auto">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize tracking-wide ${statusColorMap[li.status?.toLowerCase?.()] || statusColorMap['default']}`}>{li.status}</span>
                              <div className="h-8 border-l border-gray-200 mx-2" />
                              <div className="flex flex-row flex-wrap md:flex-nowrap gap-6 items-center">
                                <div>
                                  <div className="text-xs text-gray-500 font-medium">Denomination</div>
                                  <div className="text-base font-bold text-brand">{li.denominationAmount}</div>
                                </div>
                                <div>
                                  <div className="text-xs text-gray-500 font-medium">Quantity</div>
                                  <div className="text-base font-bold text-brand">{li.quantity}</div>
                                </div>
                                <div>
                                  <div className="text-xs text-gray-500 font-medium">Used</div>
                                  <div className="text-base font-bold text-blue-700">{li.usedQuantity}</div>
                                </div>
                                <div>
                                  <div className="text-xs text-gray-500 font-medium">Remaining</div>
                                  <div className="text-base font-bold text-teal-600">{li.remainingQuantity}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {!loading && !error && purchaseOrders && purchaseOrders.transactions.length === 0 && (
          <div className="bg-white rounded-xl p-6 flex flex-col justify-center min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
            <div className="flex items-center gap-2 justify-center text-gray-500">
              <span>No orders found.</span>
            </div>
          </div>
        )}
      </div>

      {/* Invoice Preview Side Panel */}
      {invoicePreview && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-30 z-40"
            onClick={() => setInvoicePreview(null)}
          />
          {/* Side Panel */}
          <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-2xl z-50 flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Hash className="w-5 h-5 text-gray-400" />
                {invoicePreview.poKey}
              </h2>
              <button
                className="text-gray-500 hover:text-red-500 text-xl font-bold px-2 py-1 rounded transition-colors"
                onClick={() => setInvoicePreview(null)}
              >
                ×
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4 flex items-center justify-center">
              <iframe
                src={invoicePreview ? invoicePreview.url : ''}
                title="Invoice PDF"
                className="w-full h-[80vh] rounded border"
                frameBorder={0}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PurchaseOrdersPage; 